// src/components/ui/PinInput.tsx
import { useState, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'

interface PinInputProps {
  length?: number
  onComplete: (pin: string) => void
  error?: string
}

export function PinInput({ length = 4, onComplete, error }: PinInputProps) {
  const [digits, setDigits] = useState<string[]>([])
  const maxLen = length

  const handleDigit = (digit: string) => {
    if (digits.length >= maxLen) return
    const next = [...digits, digit]
    setDigits(next)
    if (next.length === maxLen) {
      onComplete(next.join(''))
    }
  }

  const handleDelete = () => {
    setDigits((prev) => prev.slice(0, -1))
  }

  const numpadKeys = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '', '0', 'del']

  return (
    <div className="flex flex-col items-center gap-6">
      {/* Dots */}
      <div className="flex gap-3">
        {Array.from({ length: maxLen }).map((_, i) => (
          <motion.div
            key={i}
            animate={{
              scale: i === digits.length ? [1, 1.2, 1] : 1,
              backgroundColor: i < digits.length ? '#4ECDC4' : '#E2E8F0',
            }}
            transition={{ duration: 0.2 }}
            className="w-5 h-5 rounded-full"
          />
        ))}
      </div>

      {/* Error */}
      <AnimatePresence>
        {error && (
          <motion.p
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="text-coral font-body text-base"
          >
            {error}
          </motion.p>
        )}
      </AnimatePresence>

      {/* Numpad */}
      <div className="grid grid-cols-3 gap-3 w-full max-w-[280px]">
        {numpadKeys.map((key) => {
          if (key === '') return <div key="empty" />
          if (key === 'del') {
            return (
              <motion.button
                key="del"
                whileTap={{ scale: 0.9 }}
                onClick={handleDelete}
                className="h-16 rounded-2xl bg-white/50 font-heading text-xl text-ink flex items-center justify-center"
              >
                ←
              </motion.button>
            )
          }
          return (
            <motion.button
              key={key}
              whileTap={{ scale: 0.9 }}
              onClick={() => handleDigit(key)}
              className="h-16 rounded-2xl bg-white shadow-sm font-heading text-2xl text-ink
                         hover:bg-white/80 active:bg-turquoise/20 transition-colors"
            >
              {key}
            </motion.button>
          )
        })}
      </div>
    </div>
  )
}
